import { useState, useEffect, useRef } from 'react'
import { Loader2, RefreshCw, AlertCircle, X } from 'lucide-react'
import { useBrand } from '../hooks/useBrand'
import { useAccount } from '../hooks/useAccount'
import { supabase } from '../lib/supabase'
import { syncSubscriptionWithWhop } from '../lib/whopSync'
import { PINK } from '../lib/theme'

const POLL_MS = 2000
const MAX_ATTEMPTS = 30      // 30 × 2s = 60s before showing the "taking longer" state
const SYNC_EVERY_ATTEMPTS = 5 // re-hit /api/whop/sync every 10s while waiting

// Gate for the return leg of the Whop checkout flow.
//
// Whop redirects the user back to the app (?from=checkout) as soon as payment
// completes, but brand_configs is only written when either the Whop webhook
// fires or our /api/whop/sync endpoint pulls the membership — both usually
// within seconds. Without this gate the user lands on a HiringGate-wrapped
// route while the brand context still holds the pre-payment row, gets bounced
// to /pricing, and concludes the payment failed.
//
// While ?from=checkout is present and the brand has no whop_subscription_id
// yet, this component replaces the routes with an "activating" screen,
// triggers a Whop sync immediately (so activation doesn't depend on webhook
// latency), and polls reloadSilent() — refreshing the SHARED brand context,
// so HiringGate, PlanBadge, and everything else flip together the moment the
// row updates. (An older version of this poll lived inside Sidebar's
// PlanBadge and only updated the badge's local state — the gates never saw
// the new row.)
//
// Checkout returns that DON'T carry ?from=checkout (Whop plan-level settings
// can override the redirect_url we register — seen with the annual plan) are
// covered by PricingPage in App.tsx, which runs the same Whop sync before
// showing the paywall.
//
// Whop Elements sends the buyer back to the same return URL after an off-site
// payment step (3DS, bank page) WHATEVER the outcome, appending
// `payment`/`setup` + `status` (succeeded | failed | canceled). A failed or
// canceled step must not show "Payment confirmed" — nothing was charged and no
// subscription is coming — so it skips the activation screen and says so.
function stripCheckoutParams() {
  const url = new URL(window.location.href)
  for (const key of ['from', 'agency', 'staging_bypass', 'payment', 'setup', 'status']) {
    url.searchParams.delete(key)
  }
  window.history.replaceState({}, '', url.toString())
}

function checkoutDidNotComplete(params: URLSearchParams): boolean {
  const status = params.get('status')
  return status === 'failed' || status === 'canceled'
}

export default function CheckoutActivation({ children }: { children: React.ReactNode }) {
  const { brand, reloadSilent } = useBrand()
  const { account, isAgency, refreshAccount } = useAccount()
  // Agency checkout returns carry ?agency=1: the $997 sub lands on the ACCOUNT,
  // not the brand row, so we watch the account here instead of brand.whop_*.
  const isAgencyReturn = new URLSearchParams(window.location.search).get('agency') === '1'
  const subscribed = isAgencyReturn
    ? (isAgency && !!account?.whop_subscription_id)
    : !!brand?.whop_subscription_id

  // Captured once per mount: did this page load come back from a Whop checkout
  // whose off-site payment step failed or was abandoned?
  const [paymentDeclined, setPaymentDeclined] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('from') === 'checkout' && checkoutDidNotComplete(params)
  })

  // Captured once per mount: did this page load come back from Whop checkout?
  const [awaitingWebhook, setAwaitingWebhook] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('from') !== 'checkout') return false
    if (checkoutDidNotComplete(params)) {
      stripCheckoutParams()
      return false
    }
    // Dev-mode bypass (see UpgradeModal/Onboarding): no real checkout
    // happened, so there is no membership to wait for.
    if (import.meta.env.DEV) {
      try {
        if (sessionStorage.getItem('dev_bypass_subscription') === '1') {
          stripCheckoutParams()
          return false
        }
      } catch { /* storage disabled */ }
    }
    return true
  })
  const [timedOut, setTimedOut] = useState(false)
  const attemptsRef = useRef(0)
  const brandId = brand?.id

  // Subscription landed (or had already landed before we got here): mark
  // onboarding complete (we no longer flip it before the Whop redirect, so this
  // is where a paid signup's onboarding finishes), clean the URL so a manual
  // reload doesn't re-enter the activation screen, and let the routes render.
  useEffect(() => {
    if (!(awaitingWebhook && subscribed)) return
    let cancelled = false
    ;(async () => {
      if (brand?.id && !brand.onboarding_completed) {
        await supabase.from('brand_configs').update({ onboarding_completed: true }).eq('id', brand.id)
        if (!cancelled) await reloadSilent()
      }
      // NOTE: cancelling the old per-brand $497 on a brand→agency upgrade is
      // handled entirely in the WEBHOOK (handleMembershipActivated →
      // cancelSupersededBrandSubs), after the account is switched to agency. We
      // deliberately do NOT trigger it from the frontend here.
      if (cancelled) return
      stripCheckoutParams()
      setAwaitingWebhook(false)
    })()
    return () => { cancelled = true }
  }, [awaitingWebhook, subscribed, brand?.id, brand?.onboarding_completed, reloadSilent])

  useEffect(() => {
    if (!awaitingWebhook || subscribed || timedOut) return

    // AGENCY return: the membership webhook (or the staging bypass) flips the
    // ACCOUNT to type='agency' + records the $997 sub. There's no brand-scoped
    // sync to call — just refresh the account context until it lands.
    if (isAgencyReturn) {
      refreshAccount()
      const interval = setInterval(() => {
        attemptsRef.current++
        refreshAccount()
        if (attemptsRef.current >= MAX_ATTEMPTS) setTimedOut(true)
      }, POLL_MS)
      return () => clearInterval(interval)
    }

    if (!brandId) return
    // Brand return: don't just wait for the webhook — pull the membership from
    // Whop right away. The sync endpoint writes the same brand_configs row the
    // webhook does, so the reloadSilent() below picks it up within one poll tick.
    syncSubscriptionWithWhop(brandId).then(ok => { if (ok) reloadSilent() })

    const interval = setInterval(async () => {
      attemptsRef.current++
      if (attemptsRef.current % SYNC_EVERY_ATTEMPTS === 0) {
        await syncSubscriptionWithWhop(brandId)
      }
      await reloadSilent()
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setTimedOut(true)
      }
    }, POLL_MS)
    return () => clearInterval(interval)
  }, [awaitingWebhook, subscribed, timedOut, brandId, reloadSilent, isAgencyReturn, refreshAccount])

  if (!awaitingWebhook || subscribed) {
    return (
      <>
        {paymentDeclined && <PaymentDeclinedNotice onDismiss={() => setPaymentDeclined(false)} />}
        {children}
      </>
    )
  }

  return (
    <div style={{
      minHeight: '60vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      textAlign: 'center',
      padding: 24,
    }}>
      {timedOut ? (
        <>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>
            Payment received — activation is taking longer than usual
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', maxWidth: 420 }}>
            Your subscription is confirmed on our payment provider's side and
            should activate here any moment.
          </div>
          <button
            onClick={() => { attemptsRef.current = 0; setTimedOut(false) }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 24px',
              background: PINK,
              color: '#fff',
              border: 'none',
              borderRadius: 999,
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(251, 58, 174, 0.35)',
            }}
          >
            <RefreshCw size={14} />
            Check again
          </button>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            Still not active after a few minutes? Contact support@brandsmeetcreators.com — you will not be charged twice.
          </div>
        </>
      ) : (
        <>
          <Loader2 size={32} className="animate-spin" style={{ color: PINK }} />
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>
            Activating your subscription
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', maxWidth: 380 }}>
            Payment confirmed — this usually takes a few seconds.
          </div>
        </>
      )}
    </div>
  )
}

function PaymentDeclinedNotice({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        margin: '0 0 16px',
        padding: '12px 14px',
        border: `1px solid ${PINK}`,
        borderRadius: 10,
        background: 'var(--bg-secondary)',
        color: 'var(--text-primary)',
        fontSize: 14,
      }}
    >
      <AlertCircle size={18} style={{ color: PINK, flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1 }}>
        Your payment wasn't completed, so you were not charged. You can try
        checkout again with the same or a different card.
      </div>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 0 }}
      >
        <X size={16} />
      </button>
    </div>
  )
}
